import { useCallback, useState } from 'react';
import { apiGet } from '../api';
import { parseApiError } from '../apiError';
import { trackEvent } from '../analytics/plausible';

export type OS = 'windows' | 'mac';
export type Variant = 'installer' | 'portable';

// Only ever returns windows or mac: it answers "which installer", not "what is this".
// A phone gets one of those answers too, so every caller must also gate on
// runsDesktopApp(deviceKind()); otherwise it offers or badges an installer for a device
// that cannot run it (DownloadsPage's platform grid, the bookshelf start panel).
export function detectOS(): OS {
  if (typeof navigator === 'undefined') return 'windows';
  const ua = navigator.userAgent || '';
  const plat = (navigator as any).platform || '';
  if (/Mac/i.test(plat) || /Mac OS X/i.test(ua) || /iP(hone|ad|od)/i.test(ua)) return 'mac';
  return 'windows';
}

// The name Windows shows on SmartScreen's App line. The browser saves the installer
// under the last part of the download link (the objects carry no Content-Disposition),
// e.g. Chaptery-Setup-1.2.7-win.exe. The portable build is run from its unzipped
// folder, where the program is Chaptery.exe. Null when the link names no .exe: the
// drawing then leaves the line out rather than showing a name Windows will not.
export function smartScreenAppName(url: string, variant: Variant): string | null {
  if (variant === 'portable') return 'Chaptery.exe';
  try {
    const name = decodeURIComponent(new URL(url).pathname.split('/').pop() || '');
    if (/\.exe$/i.test(name)) return name;
  } catch { /* fall through */ }
  return null;
}

export interface SmartScreenState { app: string | null; portable: boolean; trigger: HTMLElement | null }

/**
 * Starting an installer download, shared by /downloads and the empty bookshelf (#1124), so
 * the two cannot drift: the same request, the same messages, the same SmartScreen help
 * on Windows (#839). `from` tags the Plausible event with where it started.
 */
export function useInstallerDownload(from: 'bookshelf' | 'downloads') {
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [smartScreen, setSmartScreen] = useState<SmartScreenState | null>(null);
  const [started, setStarted] = useState<OS | null>(null);

  // `trigger` is the button pressed, for the SmartScreen dialog to hand focus back to.
  const start = useCallback(async (variant: Variant = 'installer', platform: OS = 'windows', trigger: HTMLElement | null = null): Promise<boolean> => {
    setDownloading(true);
    setDownloadError(null);
    const apiPlatform = platform === 'mac' ? 'mac' : 'win';
    try {
      const response = await apiGet(`/updates/download?platform=${apiPlatform}&variant=${variant}`);
      const data = (await response.body.json()) as { url: string };
      window.location.href = data.url;
      setStarted(platform);
      // Analytics must never turn a running download into "Download failed".
      try { trackEvent('Start Download', { os: platform, from }); } catch { /* analytics is best effort */ }
      if (platform === 'windows') {
        setSmartScreen({ app: smartScreenAppName(data.url, variant), portable: variant === 'portable', trigger });
      }
      return true;
    } catch (err: unknown) {
      setStarted(null);
      const { code } = await parseApiError(err);
      setDownloadError(
        code === 'SUBSCRIPTION_REQUIRED'
          ? 'An active subscription is required to download.'
          : 'Download failed. Please try again or contact support.',
      );
      return false;
    } finally {
      setDownloading(false);
    }
  }, [from]);

  const closeSmartScreen = useCallback(() => setSmartScreen(null), []);

  return { downloading, downloadError, smartScreen, closeSmartScreen, started, start };
}
